import React from 'react';
import { A, HORIZONTAL, VERTICAL, SIMBOLO } from './logoGeometria.js';

/**
 * Logo + Brujula — GUIA
 * El logo: GU en Brote, la I y la A en Orquídea (el par complementario).
 * Sobre fondo claro: <Logo sobreClaro/> pone GU en negro y deja IA en Orquídea
 * (el Brote sobre claro no se lee: 1,6:1 sobre blanco). Full color va sobre oscuro.
 * Variantes: horizontal · vertical 2×2 · símbolo (la A sola). Ver `logoGeometria.js`.
 * La A es una brújula: <Brujula/> la anima. Gira libre sobre el centro de su
 * agujero como una aguja magnética (péndulo amortiguado: I·θ'' = -k·sin(θ-θc) - c·θ'),
 * el roce la frena, se capta señalando a GUIA y, sin pausa, se ajusta hacia
 * donde va (su postura de logo). ≈5 s. Aprobada por An el 13/10/2026.
 *
 * Geometría tomada del Figma (nodo 53:8): la A va rotada 27° a la izquierda y
 * apoyada un poco por debajo del renglón de las letras.
 */
const RAD = Math.PI / 180;
const HOYO = [55.1636, 60.9244]; // centro del agujero de la A, en su propio dibujo
// Giro respecto de la postura final para apuntar a lo que la A señala:
// a las letras en el horizontal; a la cabeza de la I en el 2×2 (el símbolo usa el del horizontal).
const HACIA = { horizontal: -65.5 * RAD, vertical: 32 * RAD, simbolo: -65.5 * RAD };
const GEOM = { horizontal: HORIZONTAL, vertical: VERTICAL, simbolo: SIMBOLO };
// Pivote = el agujero de la A, ya acomodado en su postura de logo (matriz de cada variante).
const pivote = (a) => [a[0] * HOYO[0] + a[2] * HOYO[1] + a[4], a[1] * HOYO[0] + a[3] * HOYO[1] + a[5]];

/**
 * Logo — tres variantes, dos fondos.
 * @param {'horizontal'|'vertical'|'simbolo'} variante  horizontal (principal) · vertical 2×2 (G U arriba, I A abajo,
 *   la A apunta a la cabeza de la I) · simbolo (la A sola: avatar, ícono).
 * @param {boolean} sobreClaro  GU en negro e IA en Orquídea. Sin esto, GU va en Brote (fondos oscuros).
 */
export function Logo({ needleRef, variante = 'horizontal', sobreClaro = false, style, ...props }) {
  const g = GEOM[variante] ?? HORIZONTAL;
  const gu = sobreClaro ? 'var(--guia-logo-gu-sobre-claro)' : 'var(--guia-brote)';
  return (
    <svg viewBox={g.viewBox} role="img" aria-label="GUIA" style={{ width: '100%', height: 'auto', overflow: 'visible', ...style }} {...props}>
      {g.letras.map((l, n) => (
        <path key={n} d={l.d} transform={l.transform} fill={l.gu ? gu : 'var(--guia-orquidea)'} />
      ))}
      <g ref={needleRef}>
        <path d={A} fill="var(--guia-orquidea)" fillRule="evenodd" transform={`matrix(${g.a.join(' ')})`} />
      </g>
    </svg>
  );
}

/**
 * @param {'horizontal'|'vertical'|'simbolo'} variante  La misma que `Logo`. En el 2×2 la aguja termina apuntando a la I.
 * @param {number} playKey  Cambialo para repetir la animación.
 * @param {() => void} onDone  Se llama cuando la aguja queda en su postura final.
 */
export default function Brujula({ variante = 'horizontal', playKey = 0, onDone, ...props }) {
  const needle = React.useRef(null);

  React.useEffect(() => {
    const el = needle.current;
    if (!el) return undefined;
    const P = pivote((GEOM[variante] ?? HORIZONTAL).a);
    const draw = (th) => el.setAttribute('transform', `rotate(${th / RAD} ${P[0]} ${P[1]})`);
    draw(0);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { onDone?.(); return undefined; }

    const s = { th: 0, w: 30, target: HACIA[variante] ?? HACIA.horizontal, k: 24, c: 1.8, fase: 0, last: null };
    let raf;
    const step = (ts) => {
      if (s.last === null) s.last = ts;
      const dt = Math.min((ts - s.last) / 1000, 0.05); s.last = ts;
      const h = 1 / 480;
      for (let i = 0, n = Math.round(dt / h); i < n; i++) {
        s.w += (-s.k * Math.sin(s.th - s.target) - s.c * s.w) * h;
        s.th += s.w * h;
      }
      const dif = Math.abs(Math.sin(s.th - s.target));
      if (s.fase === 0 && Math.abs(s.w) < 0.9 && dif < 0.2) { // captó GUIA: sin pausa, se ajusta
        s.target = Math.round(s.th / (2 * Math.PI)) * 2 * Math.PI;
        s.k = 16; s.c = 7; s.fase = 1;
      } else if (s.fase === 1 && Math.abs(s.w) < 0.05 && dif < 0.03) {
        draw(s.target); onDone?.(); return;
      }
      draw(s.th);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playKey, variante]); // eslint-disable-line react-hooks/exhaustive-deps

  return <Logo needleRef={needle} variante={variante} {...props} />;
}
